<template>
  <div class="app-container fb-debug-page">
    <!-- 顶部工具栏 -->
    <el-card shadow="never" class="toolbar-card">
      <div slot="header" class="toolbar-header">
        <div class="toolbar-title">
          <i class="el-icon-monitor"></i>
          <span>Facebook API 调试工具</span>
          <el-tag size="mini" type="danger" effect="plain" style="margin-left: 8px;">仅管理员</el-tag>
        </div>
        <el-tag v-if="tokenList.length > 0" size="small" type="info" effect="plain">
          共 {{ tokenList.length }} 个 Token
        </el-tag>
      </div>

      <!-- 接口选择 -->
      <div class="api-selector-row">
        <div class="api-selector-label">调试接口</div>
        <div class="api-selector-options">
          <el-radio-group v-model="selectedApi" size="small" @change="handleApiChange">
            <el-radio-button v-for="api in apiOptions" :key="api.key" :label="api.key">
              <i :class="api.icon"></i> {{ api.shortLabel }}
            </el-radio-button>
          </el-radio-group>
        </div>
      </div>

      <!-- 动态参数表单 -->
      <transition name="el-fade-in">
        <div v-if="selectedApi" class="params-row">
          <el-form :inline="true" size="small">
            <el-form-item v-if="currentApiConfig.needToken" label="Token" class="param-item">
              <el-select
                v-model="params.tokenId"
                placeholder="输入 ID / 名称搜索 Token"
                style="width: 440px"
                filterable
                clearable
                :filter-method="filterTokens"
                @visible-change="handleTokenDropdownVisibleChange"
              >
                <el-option
                  v-for="t in filteredTokenList"
                  :key="t.tokenId"
                  :value="t.tokenId"
                >
                  <div class="token-option">
                    <span class="token-id">ID:{{ t.tokenId }}</span>
                    <span class="token-name">{{ t.tokenName || '未命名' }}</span>
                    <el-tag :type="t.tokenType === 1 ? 'primary' : 'success'" size="mini" effect="plain">
                      {{ tokenTypeLabel(t.tokenType) }}
                    </el-tag>
                    <el-tag :type="t.status === 1 ? 'success' : 'danger'" size="mini" effect="plain">
                      {{ t.status === 1 ? '有效' : '无效' }}
                    </el-tag>
                  </div>
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item v-if="currentApiConfig.needAppToken" label="App Token" class="param-item">
              <el-input
                v-model="params.appAccessToken"
                placeholder="留空则使用内置默认 App Token"
                style="width: 400px"
                clearable
              >
                <template slot="prepend"><i class="el-icon-key"></i></template>
              </el-input>
            </el-form-item>
            <el-form-item v-if="currentApiConfig.needAdAccountId" label="广告账户" class="param-item">
              <el-select
                v-model="params.adAccountId"
                placeholder="输入 ID / 名称搜索账户"
                style="width: 400px"
                filterable
                clearable
                :filter-method="filterAccounts"
                @visible-change="handleAccountDropdownVisibleChange"
              >
                <el-option
                  v-for="a in filteredAccountList"
                  :key="a.accountId"
                  :value="a.accountId"
                >
                  <div class="token-option">
                    <span class="token-id">{{ a.accountId }}</span>
                    <span class="token-name">{{ a.accountName || '未命名' }}</span>
                  </div>
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item v-if="currentApiConfig.needBatchId" label="批次号" class="param-item">
              <el-input
                v-model="params.batchId"
                placeholder="输入 Facebook 批次 ID"
                style="width: 300px"
                clearable
              >
                <template slot="prepend"><i class="el-icon-document-copy"></i></template>
              </el-input>
            </el-form-item>
            <el-form-item class="param-item">
              <el-button type="primary" icon="el-icon-search" :loading="loading" @click="handleQuery">
                {{ currentApiConfig.actionLabel || '查询' }}
              </el-button>
              <el-button icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
            </el-form-item>
          </el-form>
          <!-- 接口说明 -->
          <div v-if="currentApiConfig.desc" class="api-desc">
            <i class="el-icon-info"></i> {{ currentApiConfig.desc }}
          </div>
        </div>
      </transition>
    </el-card>

    <!-- 结果展示 -->
    <el-card v-if="hasResult" shadow="never" class="result-card">
      <div slot="header" class="result-header">
        <div class="result-title">
          <i class="el-icon-data-analysis"></i>
          <span>查询结果</span>
          <el-tag v-if="tableData.length > 0" size="small" type="success" effect="plain" style="margin-left: 8px;">
            {{ tableData.length }} 条记录
          </el-tag>
        </div>
        <div class="result-actions">
          <el-button
            v-if="nextPageUrl"
            type="primary"
            size="mini"
            icon="el-icon-bottom"
            :loading="pagingLoading"
            @click="handleNextPage"
          >
            加载下一页
          </el-button>
          <el-divider v-if="nextPageUrl" direction="vertical" />
          <el-button type="text" size="mini" icon="el-icon-copy-document" @click="handleCopyJson">
            复制 JSON
          </el-button>
          <el-radio-group v-model="viewMode" size="mini" style="margin-left: 8px;">
            <el-radio-button label="table"><i class="el-icon-s-grid"></i> 表格</el-radio-button>
            <el-radio-button label="json"><i class="el-icon-document"></i> JSON</el-radio-button>
          </el-radio-group>
        </div>
      </div>

      <!-- FB 错误提示 -->
      <div v-if="fbError" class="fb-error-block">
        <el-alert :title="fbError.summary" type="error" show-icon :closable="false">
          <div v-if="fbError.detail" class="fb-error-detail">
            <div v-for="(val, key) in fbError.detail" :key="key" class="error-field">
              <span class="error-label">{{ key }}:</span>
              <span class="error-value">{{ typeof val === 'object' ? JSON.stringify(val) : val }}</span>
            </div>
          </div>
        </el-alert>
      </div>

      <!-- 同步结果提示 -->
      <div v-if="syncMessage" style="margin-bottom: 16px;">
        <el-alert :title="syncMessage" type="success" show-icon :closable="false" />
      </div>

      <!-- 表格视图 -->
      <div v-if="viewMode === 'table'" class="table-view">
        <template v-if="tableData.length > 0">
          <el-table
            :data="tableData"
            border
            stripe
            size="small"
            max-height="560"
            style="width: 100%"
            highlight-current-row
            :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 600 }"
          >
            <el-table-column type="index" label="#" width="50" align="center" />
            <el-table-column
              v-for="col in tableColumns"
              :key="col"
              :prop="col"
              :label="col"
              min-width="150"
              show-overflow-tooltip
            >
              <template slot-scope="scope">
                <span v-if="isJsonString(scope.row[col])" class="cell-json" @click="showJsonDetail(scope.row[col])">
                  {{ truncate(scope.row[col], 80) }}
                </span>
                <span v-else-if="isBooleanLike(scope.row[col])">
                  <el-tag :type="scope.row[col] === 'true' ? 'success' : 'danger'" size="mini" effect="plain">
                    {{ scope.row[col] }}
                  </el-tag>
                </span>
                <span v-else>{{ scope.row[col] }}</span>
              </template>
            </el-table-column>
          </el-table>
          <div v-if="nextPageUrl" class="load-more-hint">
            <i class="el-icon-more"></i> 已加载 {{ tableData.length }} 条记录，点击「加载下一页」获取更多
          </div>
        </template>

        <!-- 嵌套分区展示 -->
        <template v-else-if="nestedSections.length > 0">
          <div v-for="section in nestedSections" :key="section.key" class="nested-section">
            <div class="section-title">
              <i class="el-icon-folder-opened"></i>
              {{ section.key }}
              <el-tag size="mini" type="info" effect="plain">{{ section.data.length }} 条</el-tag>
            </div>
            <el-table
              :data="section.data"
              border
              stripe
              size="small"
              max-height="400"
              style="width: 100%"
              :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 600 }"
            >
              <el-table-column type="index" label="#" width="50" align="center" />
              <el-table-column
                v-for="col in section.columns"
                :key="col"
                :prop="col"
                :label="col"
                min-width="150"
                show-overflow-tooltip
              />
            </el-table>
          </div>
        </template>

        <el-empty v-else description="当前数据无法以表格展示，请切换到 JSON 视图" :image-size="80" />
      </div>

      <!-- JSON 视图 -->
      <div v-if="viewMode === 'json'" class="json-view">
        <pre class="json-viewer" v-html="highlightedJson"></pre>
      </div>
    </el-card>

    <!-- JSON 详情弹窗 -->
    <el-dialog title="JSON 详情" :visible.sync="jsonDetailVisible" width="720px" append-to-body>
      <pre class="json-viewer json-dialog-content" v-html="jsonDetailHighlighted"></pre>
    </el-dialog>
  </div>
</template>

<script>
import {
  listDebugTokens,
  listDebugAccounts,
  debugToken,
  queryPages,
  queryBmAndPages,
  queryPixels,
  queryBatchRequests,
  queryAccountPixels,
  queryPromotePages,
  syncBmAndAccounts,
  fetchNextPage
} from '@/api/system/fbDebug'

const API_OPTIONS = [
  {
    key: 'debugToken',
    label: '1. Debug Token（查询权限）',
    shortLabel: 'Debug Token',
    icon: 'el-icon-unlock',
    needToken: true,
    needAppToken: true,
    needBatchId: false,
    desc: '查询指定 Token 的权限范围、有效期、所属应用等信息'
  },
  {
    key: 'queryPages',
    label: '2. 查询公共主页列表',
    shortLabel: '公共主页',
    icon: 'el-icon-document',
    needToken: true,
    needAppToken: false,
    needBatchId: false,
    desc: '调用 /me/accounts 获取该 Token 可访问的公共主页列表'
  },
  {
    key: 'queryBmAndPages',
    label: '3. 查询 BM 和公共主页',
    shortLabel: 'BM + 主页',
    icon: 'el-icon-office-building',
    needToken: true,
    needAppToken: false,
    needBatchId: false,
    desc: '调用 /me?fields=accounts,businesses 获取 BM 和公共主页'
  },
  {
    key: 'queryPixels',
    label: '4. 查询 Pixel 列表',
    shortLabel: 'Pixel',
    icon: 'el-icon-aim',
    needToken: true,
    needAppToken: false,
    needBatchId: false,
    desc: '调用 /me/adaccounts?fields=adspixels 获取各广告账户下的 Pixel'
  },
  {
    key: 'queryBatchRequests',
    label: '5. 查询批次请求',
    shortLabel: '批次请求',
    icon: 'el-icon-tickets',
    needToken: true,
    needAppToken: false,
    needBatchId: true,
    needAdAccountId: false,
    desc: '查询指定批次 ID 下的所有请求状态'
  },
  {
    key: 'queryAccountPixels',
    label: '6. 按账户查 Pixel',
    shortLabel: '账户Pixel',
    icon: 'el-icon-aim',
    needToken: true,
    needAppToken: false,
    needBatchId: false,
    needAdAccountId: true,
    desc: '调用 /{act_xxx}/adspixels 查询指定广告账户的 Pixel 列表'
  },
  {
    key: 'queryPromotePages',
    label: '7. 按账户查投放Pages',
    shortLabel: '投放Pages',
    icon: 'el-icon-postcard',
    needToken: true,
    needAppToken: false,
    needBatchId: false,
    needAdAccountId: true,
    desc: '调用 /{act_xxx}/promote_pages 查询指定广告账户可用于投放的公共主页'
  },
  {
    key: 'syncBmAndAccounts',
    label: '8. 手动触发 BM + 账户同步',
    shortLabel: '同步',
    icon: 'el-icon-refresh',
    needToken: true,
    needAppToken: false,
    needBatchId: false,
    needAdAccountId: false,
    actionLabel: '执行同步',
    desc: '手动触发该 Token 的 BM + 广告账户同步'
  }
]

const API_FUNCTIONS = {
  debugToken,
  queryPages,
  queryBmAndPages,
  queryPixels,
  queryBatchRequests,
  queryAccountPixels,
  queryPromotePages,
  syncBmAndAccounts
}

export default {
  name: 'FbDebug',
  data() {
    return {
      selectedApi: '',
      apiOptions: API_OPTIONS,
      tokenList: [],
      filteredTokenList: [],
      accountList: [],
      filteredAccountList: [],
      params: {
        tokenId: null,
        appAccessToken: '',
        batchId: '',
        adAccountId: ''
      },
      loading: false,
      pagingLoading: false,
      rawResult: null,
      viewMode: 'table',
      nextPageUrl: null,
      syncMessage: '',
      tableData: [],
      tableColumns: [],
      nestedSections: [],
      fbError: null,
      jsonDetailVisible: false,
      jsonDetailContent: ''
    }
  },
  computed: {
    currentApiConfig() {
      return this.apiOptions.find(a => a.key === this.selectedApi) || {}
    },
    hasResult() {
      return this.rawResult !== null || this.syncMessage || this.fbError
    },
    formattedJson() {
      if (!this.rawResult) return ''
      return JSON.stringify(this.rawResult, null, 2)
    },
    highlightedJson() {
      return this.syntaxHighlight(this.formattedJson)
    },
    jsonDetailHighlighted() {
      return this.syntaxHighlight(this.jsonDetailContent)
    }
  },
  created() {
    this.loadTokens()
    this.loadAccounts()
  },
  methods: {
    async loadTokens() {
      try {
        const res = await listDebugTokens()
        if (res.code === 0) {
          this.tokenList = res.data || []
          this.filteredTokenList = this.tokenList.slice()
        }
      } catch (e) {
        console.error('加载 Token 列表失败', e)
      }
    },

    async loadAccounts() {
      try {
        var res = await listDebugAccounts()
        if (res.code === 0) {
          this.accountList = res.data || []
          this.filteredAccountList = this.accountList.slice()
        }
      } catch (e) {
        console.error('加载广告账户列表失败', e)
      }
    },

    filterAccounts(query) {
      if (!query) {
        this.filteredAccountList = this.accountList.slice()
        return
      }
      var keyword = query.toLowerCase()
      this.filteredAccountList = this.accountList.filter(function (a) {
        var idMatch = a.accountId && a.accountId.toLowerCase().indexOf(keyword) !== -1
        var nameMatch = a.accountName && a.accountName.toLowerCase().indexOf(keyword) !== -1
        return idMatch || nameMatch
      })
    },

    handleAccountDropdownVisibleChange(visible) {
      if (visible) {
        this.filteredAccountList = this.accountList.slice()
      }
    },

    filterTokens(query) {
      if (!query) {
        this.filteredTokenList = this.tokenList.slice()
        return
      }
      var keyword = query.toLowerCase()
      this.filteredTokenList = this.tokenList.filter(function (t) {
        var idMatch = String(t.tokenId).indexOf(keyword) !== -1
        var nameMatch = t.tokenName && t.tokenName.toLowerCase().indexOf(keyword) !== -1
        var platformMatch = t.platformUserId && t.platformUserId.toLowerCase().indexOf(keyword) !== -1
        var accountMatch = t.adAccountId && t.adAccountId.toLowerCase().indexOf(keyword) !== -1
        return idMatch || nameMatch || platformMatch || accountMatch
      })
    },

    handleTokenDropdownVisibleChange(visible) {
      if (visible) {
        this.filteredTokenList = this.tokenList.slice()
      }
    },

    tokenTypeLabel(type) {
      if (type === 1) return 'BM级'
      if (type === 2) return '账户级'
      return '未知'
    },

    handleApiChange() {
      this.rawResult = null
      this.tableData = []
      this.tableColumns = []
      this.nestedSections = []
      this.nextPageUrl = null
      this.syncMessage = ''
      this.fbError = null
    },

    handleReset() {
      this.params = { tokenId: null, appAccessToken: '', batchId: '', adAccountId: '' }
      this.rawResult = null
      this.tableData = []
      this.tableColumns = []
      this.nestedSections = []
      this.nextPageUrl = null
      this.syncMessage = ''
      this.fbError = null
    },

    async handleQuery() {
      if (!this.selectedApi) {
        this.$message.warning('请先选择调试接口')
        return
      }
      if (this.currentApiConfig.needToken && !this.params.tokenId) {
        this.$message.warning('请选择 Token')
        return
      }
      if (this.currentApiConfig.needBatchId && !this.params.batchId) {
        this.$message.warning('请输入批次号')
        return
      }
      if (this.currentApiConfig.needAdAccountId && !this.params.adAccountId) {
        this.$message.warning('请输入广告账户 ID')
        return
      }

      this.loading = true
      this.rawResult = null
      this.tableData = []
      this.tableColumns = []
      this.nestedSections = []
      this.nextPageUrl = null
      this.syncMessage = ''
      this.fbError = null

      try {
        const fn = API_FUNCTIONS[this.selectedApi]
        const res = await fn(this.buildParams())

        if (res.code === 0) {
          if (this.selectedApi === 'syncBmAndAccounts') {
            this.syncMessage = res.data || '同步已触发'
          } else {
            this.rawResult = res.data
            this.checkFbError(res.data)
            this.parseResult(res.data)
          }
        } else {
          this.$message.error(res.message || '请求失败')
        }
      } catch (e) {
        this.$message.error('请求异常：' + (e.message || e))
      } finally {
        this.loading = false
      }
    },

    buildParams() {
      var p = {}
      if (this.currentApiConfig.needToken) {
        p.tokenId = this.params.tokenId
      }
      if (this.currentApiConfig.needAppToken && this.params.appAccessToken) {
        p.appAccessToken = this.params.appAccessToken
      }
      if (this.currentApiConfig.needBatchId) {
        p.batchId = this.params.batchId
      }
      if (this.currentApiConfig.needAdAccountId) {
        p.adAccountId = this.params.adAccountId
      }
      return p
    },

    parseResult(data) {
      if (!data) return

      this.nextPageUrl = this.extractNextUrl(data)

      if (Array.isArray(data.data)) {
        this.flattenToTable(data.data)
        return
      }

      if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
        this.flattenToTable([data.data])
        return
      }

      var sections = []
      for (var key of Object.keys(data)) {
        var val = data[key]
        if (val && typeof val === 'object' && Array.isArray(val.data)) {
          var rows = this.flattenRows(val.data)
          var cols = this.extractColumns(rows)
          sections.push({ key: key, data: rows, columns: cols })
          var sectionNext = this.extractNextUrl(val)
          if (sectionNext && !this.nextPageUrl) {
            this.nextPageUrl = sectionNext
          }
        }
      }

      if (sections.length > 0) {
        this.nestedSections = sections
        return
      }

      if (typeof data === 'object') {
        this.flattenToTable([data])
      }
    },

    flattenToTable(rows) {
      var flat = this.flattenRows(rows)
      this.tableColumns = this.extractColumns(flat)
      this.tableData = flat
    },

    flattenRows(rows) {
      return rows.map(function (row) {
        var flat = {}
        Object.keys(row).forEach(function (k) {
          var v = row[k]
          if (v === null || v === undefined) {
            flat[k] = ''
          } else if (typeof v === 'object') {
            flat[k] = JSON.stringify(v)
          } else {
            flat[k] = String(v)
          }
        })
        return flat
      })
    },

    extractColumns(rows) {
      var colSet = []
      rows.forEach(function (r) {
        Object.keys(r).forEach(function (k) {
          if (colSet.indexOf(k) === -1) {
            colSet.push(k)
          }
        })
      })
      return colSet
    },

    extractNextUrl(obj) {
      if (!obj || !obj.paging) return null
      return obj.paging.next || null
    },

    async handleNextPage() {
      if (!this.nextPageUrl) return
      this.pagingLoading = true
      try {
        var res = await fetchNextPage({ nextUrl: this.nextPageUrl })
        if (res.code === 0 && res.data) {
          this.nextPageUrl = this.extractNextUrl(res.data)
          if (Array.isArray(res.data.data)) {
            var self = this
            var newRows = this.flattenRows(res.data.data)
            newRows.forEach(function (r) {
              Object.keys(r).forEach(function (k) {
                if (self.tableColumns.indexOf(k) === -1) {
                  self.tableColumns.push(k)
                }
              })
            })
            this.tableData = this.tableData.concat(newRows)
          }
          this.rawResult = this.mergeRawResult(this.rawResult, res.data)
        } else {
          this.$message.error(res.message || '翻页失败')
        }
      } catch (e) {
        this.$message.error('翻页异常：' + (e.message || e))
      } finally {
        this.pagingLoading = false
      }
    },

    mergeRawResult(prev, next) {
      if (!prev) return next
      var merged = Object.assign({}, next)
      if (Array.isArray(prev.data) && Array.isArray(next.data)) {
        merged.data = prev.data.concat(next.data)
      }
      return merged
    },

    handleCopyJson() {
      if (!this.rawResult) return
      var text = JSON.stringify(this.rawResult, null, 2)
      navigator.clipboard.writeText(text).then(function () {
        this.$message.success('已复制到剪贴板')
      }.bind(this)).catch(function () {
        this.$message.error('复制失败，请手动复制')
      }.bind(this))
    },

    checkFbError(data) {
      if (!data || !data._fbError) return
      var errorObj = data.error
      this.fbError = {
        summary: data._errorSummary || 'Facebook API 返回错误',
        detail: errorObj && typeof errorObj === 'object' ? errorObj : null
      }
    },

    isJsonString(str) {
      if (!str || typeof str !== 'string') return false
      var s = str.trim()
      return (s.charAt(0) === '{' || s.charAt(0) === '[') && s.length > 20
    },

    isBooleanLike(str) {
      return str === 'true' || str === 'false'
    },

    truncate(str, len) {
      if (!str) return ''
      return str.length > len ? str.substring(0, len) + '...' : str
    },

    showJsonDetail(jsonStr) {
      try {
        var parsed = JSON.parse(jsonStr)
        this.jsonDetailContent = JSON.stringify(parsed, null, 2)
      } catch (e) {
        this.jsonDetailContent = jsonStr
      }
      this.jsonDetailVisible = true
    },

    syntaxHighlight(json) {
      if (!json) return ''
      var escaped = json
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      return escaped.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
        function (match) {
          var cls = 'json-number'
          if (/^"/.test(match)) {
            cls = /:$/.test(match) ? 'json-key' : 'json-string'
          } else if (/true|false/.test(match)) {
            cls = 'json-boolean'
          } else if (/null/.test(match)) {
            cls = 'json-null'
          }
          return '<span class="' + cls + '">' + match + '</span>'
        }
      )
    }
  }
}
</script>

<style scoped>
.fb-debug-page {
  max-width: 1400px;
}

/* 工具栏卡片 */
.toolbar-card {
  border-radius: 8px;
}
.toolbar-card >>> .el-card__header {
  padding: 14px 20px;
  background: linear-gradient(135deg, #f0f5ff 0%, #e8f4f8 100%);
  border-bottom: 1px solid #e4e7ed;
}
.toolbar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.toolbar-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.toolbar-title i {
  font-size: 20px;
  margin-right: 8px;
  color: #409eff;
}

/* 接口选择行 */
.api-selector-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px dashed #ebeef5;
}
.api-selector-label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
  padding-top: 7px;
  white-space: nowrap;
}
.api-selector-options {
  flex: 1;
}
.api-selector-options >>> .el-radio-button__inner {
  padding: 8px 14px;
}

/* 参数行 */
.params-row {
  margin-top: 16px;
  padding-top: 16px;
}
.param-item {
  margin-bottom: 8px !important;
}
.api-desc {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}
.api-desc i {
  margin-right: 4px;
}

/* Token 下拉选项 */
.token-option {
  display: flex;
  align-items: center;
  gap: 8px;
}
.token-id {
  color: #909399;
  font-size: 12px;
  font-family: 'Menlo', 'Monaco', 'Consolas', monospace;
  white-space: nowrap;
}
.token-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

/* 结果卡片 */
.result-card {
  margin-top: 16px;
  border-radius: 8px;
}
.result-card >>> .el-card__header {
  padding: 12px 20px;
  background: #fafafa;
}
.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.result-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #303133;
}
.result-title i {
  margin-right: 6px;
  color: #67c23a;
}
.result-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 表格视图 */
.table-view {
  min-height: 100px;
}
.load-more-hint {
  margin-top: 12px;
  padding: 8px 12px;
  background: #f0f9ff;
  border-radius: 4px;
  color: #409eff;
  font-size: 12px;
}
.load-more-hint i {
  margin-right: 4px;
}

/* 嵌套分区 */
.nested-section {
  margin-bottom: 20px;
}
.nested-section:last-child {
  margin-bottom: 0;
}
.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.section-title i {
  color: #e6a23c;
}

/* 单元格样式 */
.cell-json {
  color: #409eff;
  cursor: pointer;
  font-size: 12px;
  font-family: 'Menlo', 'Monaco', 'Consolas', monospace;
}
.cell-json:hover {
  text-decoration: underline;
}

/* FB 错误区块 */
.fb-error-block {
  margin-bottom: 16px;
}
.fb-error-detail {
  margin-top: 8px;
  padding: 10px;
  background: #fff2f0;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.8;
  font-family: 'Menlo', 'Monaco', 'Consolas', monospace;
}
.error-field {
  display: flex;
  gap: 8px;
}
.error-label {
  color: #909399;
  font-weight: 600;
  white-space: nowrap;
}
.error-value {
  color: #f56c6c;
  word-break: break-all;
}

/* JSON 视图 */
.json-view {
  min-height: 100px;
}
.json-viewer {
  background: #1e1e1e;
  border-radius: 6px;
  padding: 20px;
  font-size: 12px;
  line-height: 1.7;
  max-height: 600px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: 'Menlo', 'Monaco', 'Consolas', 'Courier New', monospace;
  color: #d4d4d4;
  margin: 0;
}
.json-dialog-content {
  max-height: 500px;
}

/* JSON 语法高亮 */
.json-viewer >>> .json-key { color: #9cdcfe; }
.json-viewer >>> .json-string { color: #ce9178; }
.json-viewer >>> .json-number { color: #b5cea8; }
.json-viewer >>> .json-boolean { color: #569cd6; }
.json-viewer >>> .json-null { color: #569cd6; }
</style>

<style>
.json-key { color: #9cdcfe; }
.json-string { color: #ce9178; }
.json-number { color: #b5cea8; }
.json-boolean { color: #569cd6; }
.json-null { color: #569cd6; }
</style>
